// frontend/src/components/common/ConfirmDialog.js
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { AlertCircle, CheckCircle } from "lucide-react";
import "./ConfirmDialog.css";

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "warning", // warning, danger, success, info
}) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "auto";
      };
    } else {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    }
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          icon: <AlertCircle className="w-12 h-12 text-[#DA291C]" />,
          titleColor: "text-[#DA291C]",
          confirmButton: "bg-[#DA291C] hover:bg-[#b22218]",
        };
      case "success":
        return {
          icon: <CheckCircle className="w-12 h-12 text-[#84BD00]" />,
          titleColor: "text-[#84BD00]",
          confirmButton: "bg-[#84BD00] hover:bg-[#6a9800]",
        };
      case "info":
        return {
          icon: <AlertCircle className="w-12 h-12 text-[#00A3E0]" />,
          titleColor: "text-[#00A3E0]",
          confirmButton: "bg-[#00A3E0] hover:bg-[#0082b3]",
        };
      case "warning":
      default:
        return {
          icon: <AlertCircle className="w-12 h-12 text-[#EE005F]" />,
          titleColor: "text-[#EE005F]",
          confirmButton: "bg-[#EE005F] hover:bg-[#c7004d]",
        };
    }
  };

  const variantStyles = getVariantStyles();

  return ReactDOM.createPortal(
    <div className="confirm-dialog-overlay" onClick={onClose}>
      <div
        className="confirm-dialog-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">{variantStyles.icon}</div>

        {/* Title */}
        <h2 className={`text-2xl font-bold text-center mb-3 ${variantStyles.titleColor}`}>
          {title}
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center mb-6">{message}</p>

        {/* Buttons */}
        <div className="flex gap-3 justify-center">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium min-w-[120px]"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-6 py-2.5 rounded-lg text-white transition-colors font-medium min-w-[120px] ${variantStyles.confirmButton}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
